import { useContext, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "react-query";
import axios from "axios";
import { toast } from "react-toastify";
import { AuthContext } from "../../../Context/AuthContext";
import StexSelect from "../../../Routes/Marketplace/components/StexSelect";
import "../../../Routes/Marketplace/components/StexSelect.css";

const fetchBanks = async (token) => {
  const response = await axios.get(`${process.env.REACT_APP_SERVER_URL}/users/paystack/banks`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  return response.data.banks || [];
};

const MerchantBankSetupForm = ({ merchant, onSuccess, onCancel, submitLabel = "Save & verify" }) => {
  const { user, token } = useContext(AuthContext);
  const queryClient = useQueryClient();
  const [bankCode, setBankCode] = useState(merchant?.bankCode || "");
  const [accountNumber, setAccountNumber] = useState(merchant?.bankAccountNumber || "");

  useEffect(() => {
    setBankCode(merchant?.bankCode || "");
    setAccountNumber(merchant?.bankAccountNumber || "");
  }, [merchant]);

  const { data: banks = [], isLoading: banksLoading, isError: banksError } = useQuery(
    ["paystackBanks"],
    () => fetchBanks(token),
    { enabled: !!token, staleTime: 1000 * 60 * 60 }
  );

  const bankOptions = useMemo(
    () => banks.map((bank) => ({ value: bank.code, label: bank.name })),
    [banks]
  );

  const canSubmit = Boolean(bankCode) && accountNumber.length === 10;

  const verifyMutation = useMutation(
    async () => {
      const response = await axios.post(
        `${process.env.REACT_APP_SERVER_URL}/users/merchants/${user.userId}/verify-bank`,
        { bankCode, accountNumber },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return response.data;
    },
    {
      onSuccess: (data) => {
        toast.success("Bank account verified and saved.");
        queryClient.invalidateQueries("businessMerchant");
        onSuccess?.(data.merchant);
      },
      onError: (error) => {
        toast.error(error.response?.data?.message || "Could not verify bank account");
      },
    }
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!canSubmit) return;
    verifyMutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="dashboard-bank-setup">
      <p className="dashboard-form-hint dashboard-form-hint--flush">
        Select your bank and enter your 10-digit account number. We verify the account name when you save.
      </p>

      {banksError && (
        <div className="dashboard-banner dashboard-banner--error">
          Could not load banks. Please refresh the page or try again later.
        </div>
      )}

      <div className="dashboard-form-grid dashboard-payout-form-grid">
        <div className="dashboard-form-field dashboard-form-field--select">
          <StexSelect
            name="bankCode"
            label="Bank *"
            value={bankCode}
            onChange={(e) => setBankCode(e.target.value)}
            options={bankOptions}
            placeholder={banksLoading ? "Loading banks…" : "Select your bank"}
            required
            disabled={banksLoading || banksError}
            searchable
          />
        </div>

        <label className="dashboard-form-field">
          <span>Account number *</span>
          <input
            type="text"
            value={accountNumber}
            onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, "").slice(0, 10))}
            inputMode="numeric"
            placeholder="10-digit NUBAN"
            maxLength={10}
            required
          />
        </label>

        <div className="dashboard-form-field dashboard-form-field--full dashboard-payout-form-actions">
          {onCancel && (
            <button type="button" className="dashboard-btn dashboard-btn--secondary" onClick={onCancel}>
              Cancel
            </button>
          )}
          <button
            type="submit"
            className="dashboard-btn dashboard-btn--primary"
            disabled={verifyMutation.isLoading || !canSubmit}
          >
            {verifyMutation.isLoading ? "Verifying…" : submitLabel}
          </button>
        </div>
      </div>
    </form>
  );
};

export default MerchantBankSetupForm;
